import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Instagram,
  Facebook,
  Phone,
  Copy,
  CheckCheck,
  Mail,
  Split,
  Ticket,
  RefreshCw,
  ScrollText,
  Info,
  Globe,
  MapPin,
  Calendar,
  Tag,
  Users,
  Share2,
  Heart,
  Bookmark,
  TrendingUp,
  Shield,
  Award,
} from "lucide-react";
import moment from "moment";
import Navbar from "../../../components/Navbar";
import PageTransition from "../../../components/PageTransition";
import PropertiesModal from "../../../components/Offers/PropertiesModal";
import { Button } from "@mui/material";

// Helper function to get location data
const getLocationData = (deal) => {
  if (deal.region_location && deal.region_location.length > 0) {
    return {
      locations: deal.region_location,
      isPartnerLocation: false
    };
  }  
  if (deal.partner_location) {
    try {
      const partnerLocations = typeof deal.partner_location === 'string' 
        ? JSON.parse(deal.partner_location) 
        : deal.partner_location;
      
      if (partnerLocations && partnerLocations.length > 0) {
        return {
          locations: partnerLocations,
          isPartnerLocation: true
        };
      }
    } catch (error) {
      console.error("Error parsing partner_location:", error);
    }
  }  
  return {
    locations: [],
    isPartnerLocation: false
  };
};

const DealDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("details");
  const [copied, setCopied] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [isPropertiesModalOpen, setIsPropertiesModalOpen] = useState(false);

  // Get deal data from route state
  const deal = location.state?.deal;
  const application = location.state?.application;

  useEffect(() => {
    if (!deal) {
      navigate("/affiliate-applications");
    }
  }, [deal, navigate]);

  const handleCopyLink = () => {
    const shareableLink = deal?.shareable_link || `https://dealboxx.promo/${application?.affiliate?.affiliate_code}/product/${deal?.deal_slug}`;
    navigator.clipboard.writeText(shareableLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const computeDiscountPercentage = (originalPrice, salePrice) => {
    if (!originalPrice || !salePrice) return 0;
    const discount = originalPrice - salePrice;
    const percentage = (discount / originalPrice) * 100;
    return Math.round(percentage);
  };

  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    return moment(expiryDate).isBefore(moment());
  };


  if (!deal) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="mb-4 text-6xl">😞</div>
          <h2 className="mb-2 text-2xl font-bold text-gray-900">
            No Deal Data
          </h2>
          <p className="mb-4 text-gray-600">
            Please navigate from the applications table to view deal details.
          </p>
          <button
            onClick={() => navigate("/affiliate-applications")}
            className="px-4 py-2 text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700"
          >
            Back to Applications
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Navbar page="Deal Details" />
      <Helmet>
        <title>
          {deal.title} - DealBoxx Vendor
        </title>
        <meta
          name="description"
          content={`View detailed information about ${deal.title} deal`}
        />
      </Helmet>

      <PageTransition className="min-h-screen bg-gray-50">
        <div className="flex flex-col px-2 py-4 mx-auto max-w-7xl min-h-screen sm:px-4 sm:py-6 lg:px-8">
          {/* Back Button */}
          <motion.div
            className="flex-shrink-0 mb-6"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <motion.button
              onClick={() => navigate("/affiliate-applications")}
              className="flex items-center text-gray-600 transition-colors hover:text-gray-900"
              whileHover={{ x: -5 }}
              whileTap={{ scale: 0.95 }}
              transition={{ duration: 0.2 }}
            >
              <ArrowLeft className="mr-2 w-5 h-5" />
              Back to Applications
            </motion.button>
          </motion.div>

          {/* Main Content */}
          <motion.div
            className="flex flex-col flex-1 gap-4 min-h-0 lg:flex-row lg:gap-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {/* Left Column - Image and Basic Info */}
            <motion.div
              className="w-full lg:w-1/2"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <div className="overflow-hidden bg-white rounded-2xl border border-gray-200 shadow-sm">
                {/* Deal Image */}
                <div className="overflow-hidden relative h-64 sm:h-80 lg:h-96">
                  <img
                    src={deal.image || "https://via.placeholder.com/600x400?text=Deal+Image"}
                    alt={deal.title}
                    className="object-cover w-full h-full"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t to-transparent from-black/20" />
                  
                  {/* Status Badge */}
                  <div className="absolute top-4 right-4">
                    <span
                      className={`px-3 py-1 text-sm font-medium text-white rounded-full ${
                        isExpired(deal.expiry_date)
                          ? "bg-red-500"
                          : deal.status === "Approved"
                          ? "bg-green-500"
                          : deal.status === "Pending"
                          ? "bg-yellow-500"
                          : "bg-gray-500"
                      }`}
                    >
                      {isExpired(deal.expiry_date) ? "Expired" : deal.status}
                    </span>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex absolute top-4 left-4 gap-2">
                    <motion.button
                      onClick={() => setIsLiked(!isLiked)}
                      className="p-2 rounded-full shadow-lg backdrop-blur-sm bg-white/90"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <Heart className={`w-5 h-5 ${isLiked ? 'text-red-500 fill-current' : 'text-gray-600'}`} />
                    </motion.button>
                    <motion.button
                      onClick={() => setIsBookmarked(!isBookmarked)}
                      className="p-2 rounded-full shadow-lg backdrop-blur-sm bg-white/90"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <Bookmark className={`w-5 h-5 ${isBookmarked ? 'text-blue-500 fill-current' : 'text-gray-600'}`} />
                    </motion.button>
                  </div>
                </div>

                {/* Deal Title and Basic Info */}
                <div className="p-6">
                  <h1 className="mb-4 text-2xl font-bold text-gray-900 sm:text-3xl">
                    {deal.title}
                  </h1>

                  {/* Price Information */}
                  {deal.price && deal.discounted_price && (
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="p-4 text-center bg-gray-50 rounded-xl">
                        <p className="mb-1 text-sm text-gray-500">Original Price</p>
                        <p className="text-xl font-bold text-gray-900">
                          ₵{parseFloat(deal.price).toFixed(2)}
                        </p>
                      </div>
                      <div className="p-4 text-center bg-green-50 rounded-xl">
                        <p className="mb-1 text-sm text-green-600">Discounted Price</p>
                        <p className="text-xl font-bold text-green-600">
                          ₵{parseFloat(deal.discounted_price).toFixed(2)}
                        </p>
                      </div>
                      <div className="p-4 text-center bg-red-50 rounded-xl">
                        <p className="mb-1 text-sm text-red-600">You Save</p>
                        <p className="text-xl font-bold text-red-600">
                          {computeDiscountPercentage(deal.price, deal.discounted_price)}%
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Quick Stats */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="flex items-center p-3 bg-blue-50 rounded-lg">
                      <Users className="mr-3 w-5 h-5 text-blue-600" />
                      <div>
                        <p className="text-sm text-blue-600">Subscribers</p>
                        <p className="font-semibold text-blue-800">{deal.subscriber_count || 0}</p>
                      </div>
                    </div>
                    <div className="flex items-center p-3 bg-purple-50 rounded-lg">
                      <TrendingUp className="mr-3 w-5 h-5 text-purple-600" />
                      <div>
                        <p className="text-sm text-purple-600">Category</p>
                        <p className="font-semibold text-purple-800">{deal.brand_category || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Expiry Information */}
                  {deal.expiry_date && (
                    <div className="p-4 bg-red-50 rounded-xl border border-red-200">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center">
                          <Calendar className="mr-2 w-5 h-5 text-red-600" />
                          <div>
                            <p className="text-sm text-red-600">
                              {isExpired(deal.expiry_date) ? "Expired On" : "Expires On"}
                            </p>
                            <p className="font-semibold text-red-800">
                              {moment(deal.expiry_date).format("DD MMM, YYYY")}
                            </p>
                          </div>
                        </div>
                        {!isExpired(deal.expiry_date) && (
                          <div className="px-3 py-1 bg-red-100 rounded-lg">
                            <p className="text-sm font-medium text-red-600">
                              {moment(deal.expiry_date).diff(moment(), "days")} days left
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>

            {/* Right Column - Detailed Information */}
            <motion.div
              className="w-full lg:w-1/2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <div className="flex flex-col h-full bg-white rounded-2xl border border-gray-200 shadow-sm">
                {/* Tabs Navigation */}
                <div className="flex-shrink-0 border-b border-gray-200">
                  <nav className="flex overflow-x-auto px-4 space-x-2 sm:px-6 sm:space-x-4 lg:overflow-x-visible lg:justify-center">
                    {[
                      { id: "details", label: "Details", icon: Info },
                      { id: "categories", label: "Categories", icon: Tag },
                      { id: "contact", label: "Contact", icon: Phone },
                      { id: "locations", label: "Locations", icon: MapPin },
                      { id: "terms", label: "T&Cs", icon: ScrollText },
                    ].map((tab) => {
                      const Icon = tab.icon;
                      return (
                        <motion.button
                          key={tab.id}
                          onClick={() => setActiveTab(tab.id)}
                          className={`py-3 px-2 border-b-2 font-medium text-xs whitespace-nowrap sm:py-4 sm:px-1 sm:text-sm flex items-center ${
                            activeTab === tab.id
                              ? "border-red-500 text-red-600"
                              : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                          }`}
                          whileHover={{ y: -2 }}
                          whileTap={{ scale: 0.95 }}
                          transition={{ duration: 0.2 }}
                        >
                          <Icon className="mr-1 w-4 h-4 sm:mr-2" />
                          {tab.label}
                        </motion.button>
                      );
                    })}
                  </nav>
                </div>

                {/* Scrollable Tab Content */}
                <div className="overflow-y-auto flex-1 p-4 min-h-0 sm:p-6">
                  <AnimatePresence mode="wait">
                    {activeTab === "details" && (
                      <motion.div
                        key="details"
                        className="space-y-4 sm:space-y-6"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ duration: 0.3 }}
                      >
                        {/* Description */}
                        <div className="p-4 bg-gray-50 rounded-xl">
                          <h3 className="mb-2 text-lg font-semibold text-gray-900">Description</h3>
                          <p className="leading-relaxed text-gray-600">
                            {deal.description || "No description available for this deal."}
                          </p>
                        </div>

                        {/* Deal Information */}
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                          <div className="p-4 bg-blue-50 rounded-xl">
                            <div className="flex items-center mb-2">
                              <Shield className="mr-2 w-5 h-5 text-blue-600" />
                              <h4 className="font-semibold text-blue-800">Redemption Type</h4>
                            </div>
                            <p className="text-sm text-blue-600">
                              {deal.redemption_type?.join(", ") || "In-store"}
                            </p>
                          </div>

                          <div className="p-4 bg-green-50 rounded-xl">
                            <div className="flex items-center mb-2">
                              <Award className="mr-2 w-5 h-5 text-green-600" />
                              <h4 className="font-semibold text-green-800">Brand</h4>
                            </div>
                            <p className="text-sm text-green-600">{deal.brand_name || "N/A"}</p>
                          </div>
                        </div>

                        {/* SMS Content */}
                        {deal.sms_content && (
                          <div className="p-4 bg-purple-50 rounded-xl">
                            <h4 className="mb-2 font-semibold text-purple-800">SMS Content</h4>
                            <p className="text-sm italic text-purple-600">"{deal.sms_content}"</p>
                          </div>
                        )}

                        {/* Properties - Button to open modal */}
                        {deal.properties && deal.properties.length > 0 && (
                          <motion.div
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className="p-4 rounded-xl border sm:p-5"
                            style={{ 
                              background: 'linear-gradient(to right, rgba(254, 105, 111, 0.1), rgba(254, 105, 111, 0.05))',
                              borderColor: 'rgba(254, 105, 111, 0.2)'
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="p-2.5 rounded-lg" style={{ backgroundColor: 'rgba(254, 105, 111, 0.15)' }}>
                                  <Tag className="w-5 h-5" style={{ color: '#FE696F' }} />
                                </div>
                                <div>
                                  <h4 className="font-semibold" style={{ color: '#FE696F' }}>Deal Properties</h4>
                                  <p className="text-sm mt-0.5" style={{ color: '#FE696F' }}>
                                    {deal.properties.length} {deal.properties.length === 1 ? 'property' : 'properties'} available
                                  </p>
                                </div>
                              </div>
                              <Button
                                variant="contained"
                                onClick={() => setIsPropertiesModalOpen(true)}
                                startIcon={<Tag className="w-4 h-4" />}
                                sx={{
                                  backgroundColor: '#FE696F',
                                  borderRadius: '12px',
                                  padding: '10px 20px',
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  boxShadow: '0 4px 6px -1px rgba(254, 105, 111, 0.3)',
                                  '&:hover': {
                                    backgroundColor: '#ff5555',
                                    boxShadow: '0 6px 8px -1px rgba(254, 105, 111, 0.4)',
                                  },
                                }}
                              >
                                View Properties
                              </Button>
                            </div>
                          </motion.div>
                        )}
                      </motion.div>
                    )}

                    {activeTab === "categories" && (
                      <motion.div
                        key="categories"
                        className="space-y-4 sm:space-y-6"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ duration: 0.3 }}
                      >
                        <div className="p-6 bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-100 shadow-sm">
                          <div className="flex gap-3 items-center mb-6">
                            <div className="w-1 h-8 bg-gradient-to-b from-pink-500 to-purple-600 rounded-full" />
                            <h3 className="text-lg font-semibold text-gray-900">Categories</h3>
                          </div>

                          <div className="flex flex-wrap gap-2 md:gap-3">
                            {deal.category?.map((category, index) => (
                              <motion.span
                                key={index}
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: index * 0.05 }}
                                className="relative group"
                              >
                                <div className="absolute inset-0 bg-gradient-to-r from-pink-500 to-purple-600 rounded-xl opacity-0 blur-lg transition-opacity duration-300 group-hover:opacity-100" />
                                <div className="relative px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-xl text-sm font-medium group-hover:border-transparent group-hover:text-white group-hover:bg-gradient-to-r from-pink-500 to-purple-600 transition-all duration-300 shadow-sm hover:shadow-lg hover:shadow-pink-500/10">
                                  {category}
                                </div>
                              </motion.span>
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {activeTab === "contact" && (
                      <motion.div
                        key="contact"
                        className="space-y-4 sm:space-y-6"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ duration: 0.3 }}
                      >
                        {/* Contact Information */}
                        <div className="space-y-4">
                          {deal.deals_phone_number && (
                            <div className="flex items-center p-4 bg-white rounded-lg border border-gray-100 transition-colors duration-200 hover:border-blue-200 hover:bg-blue-50">
                              <div className="p-2 mr-3 bg-blue-100 rounded-md">
                                <Phone className="w-5 h-5 text-blue-600" />
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 mb-0.5">Phone Number</p>
                                <a
                                  href={`tel:${deal.deals_phone_number}`}
                                  className="text-sm font-medium text-gray-900 hover:text-blue-600"
                                >
                                  {deal.deals_phone_number}
                                </a>
                              </div>
                            </div>
                          )}

                          {deal.email && (
                            <div className="flex items-center p-4 bg-white rounded-lg border border-gray-100 transition-colors duration-200 hover:border-green-200 hover:bg-green-50">
                              <div className="p-2 mr-3 bg-green-100 rounded-md">
                                <Mail className="w-5 h-5 text-green-600" />
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 mb-0.5">Email</p>
                                <a
                                  href={`mailto:${deal.email}`}
                                  className="text-sm font-medium text-gray-900 hover:text-green-600"
                                >
                                  {deal.email}
                                </a>
                              </div>
                            </div>
                          )}

                          {deal.website && (
                            <div className="flex items-center p-4 bg-white rounded-lg border border-gray-100 transition-colors duration-200 hover:border-purple-200 hover:bg-purple-50">
                              <div className="p-2.5 bg-purple-100 rounded-md mr-3">
                                <Globe className="w-5 h-5 text-purple-600" />
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 mb-0.5">Website</p>
                                <a
                                  href={deal.website}
                                  className="text-sm font-medium text-gray-900 hover:text-purple-600"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {deal.website}
                                </a>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Social Media */}
                        <div className="p-4 bg-gray-50 rounded-xl">
                          <h4 className="mb-3 text-sm font-medium text-gray-700">Social Media</h4>
                          <div className="flex gap-2">
                            {deal.instagram && (
                              <motion.a
                                href={deal.instagram}
                                className="flex items-center px-3 py-2 text-sm font-medium text-white bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                              >
                                <Instagram className="mr-2 w-4 h-4" />
                                Instagram
                              </motion.a>
                            )}
                            {deal.facebook && (
                              <motion.a
                                href={deal.facebook}
                                className="flex items-center px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                              >
                                <Facebook className="mr-2 w-4 h-4" />
                                Facebook
                              </motion.a>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {activeTab === "locations" && (
                      <motion.div
                        key="locations"
                        className="space-y-4 sm:space-y-6"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ duration: 0.3 }}
                      >
                        <div className="flex gap-3 items-center mb-4">
                          <div className="w-1 h-8 bg-gradient-to-b from-pink-500 to-purple-600 rounded-full" />
                          <h3 className="text-lg font-semibold text-gray-900">Available Locations</h3>
                        </div>

                        {(() => {
                          const { locations, isPartnerLocation } = getLocationData(deal);
                          
                          if (locations.length === 0) {
                            return (
                              <div className="flex justify-center items-center p-8 bg-gray-50 rounded-xl">
                                <p className="text-gray-500">No location information available</p>
                              </div>
                            );
                          }

                          if (isPartnerLocation) {
                            return (
                              <div className="space-y-4">
                                {locations.map((partner, partnerIndex) => (
                                  <motion.div
                                    key={`partner-${partnerIndex}`}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: partnerIndex * 0.1 }}
                                    className="overflow-hidden bg-white rounded-xl border border-blue-100 shadow-sm"
                                  >
                                    <div className="p-4 bg-blue-50 border-b border-blue-100">
                                      <div className="flex justify-between items-center">
                                        <h5 className="font-semibold text-blue-800">{partner.partner_name}</h5>
                                        <span className="px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded-full">
                                          {partner.branches.length} {partner.branches.length === 1 ? 'Branch' : 'Branches'}
                                        </span>
                                      </div>
                                    </div>
                                    
                                    <div className="p-4">
                                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                        {partner.branches.map((branch, branchIndex) => (
                                          <motion.div
                                            key={`branch-${branch.branch_slug || branchIndex}`}
                                            initial={{ opacity: 0, x: -10 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: 0.1 + (branchIndex * 0.05) }}
                                            className="overflow-hidden relative p-3 bg-white rounded-lg border border-gray-100 transition-colors duration-200 hover:border-blue-200 hover:bg-blue-50/30"
                                          >
                                            <div className="flex items-start gap-2 mb-2.5 pb-2 border-b border-gray-100">
                                              <div className="w-2 h-2 bg-blue-500 rounded-full mt-1.5 flex-shrink-0" />
                                              <div className="relative flex-1 min-w-0 group">
                                                <h6 className="font-medium text-gray-900 truncate" title={branch.name}>
                                                  {branch.name}
                                                </h6>
                                              </div>
                                            </div>
                                            
                                            <div className="space-y-2">
                                              <div className="flex gap-2 items-center text-sm text-gray-600">
                                                <Split className="flex-shrink-0 w-4 h-4 text-blue-500" />
                                                <span className="truncate" title={`${branch.region} - ${branch.location}`}>
                                                  {branch.region} - {branch.location}
                                                </span>
                                              </div>
                                              
                                              {branch.phone_number && (
                                                <div className="flex gap-2 items-center text-sm text-gray-600">
                                                  <Phone className="flex-shrink-0 w-4 h-4 text-blue-500" />
                                                  <a 
                                                    href={`tel:${branch.phone_number}`} 
                                                    className="truncate hover:text-blue-600"
                                                    title={branch.phone_number}
                                                  >
                                                    {branch.phone_number}
                                                  </a>
                                                </div>
                                              )}
                                            </div>
                                          </motion.div>
                                        ))}
                                      </div>
                                    </div>
                                  </motion.div>
                                ))}
                              </div>
                            );
                          } else {
                            if (locations.length === 1 && 
                                locations[0].location === "all" && 
                                locations[0].region === "all") {
                              return (
                                <motion.div
                                  initial={{ opacity: 0, x: -10 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: 0.1 }}
                                  className="overflow-hidden relative bg-white rounded-xl border border-gray-100 group hover:border-pink-200"
                                >
                                  <div className="p-4">
                                    <div className="flex justify-between items-center mb-3">
                                      <div className="flex gap-2 items-center">
                                        <div className="w-2 h-2 bg-green-500 rounded-full" />
                                        <h5 className="font-medium text-gray-900">All Locations</h5>
                                      </div>
                                      <span className="text-xs font-medium text-gray-500">Nationwide</span>
                                    </div>
                                    <div className="space-y-2">
                                      <div className="flex gap-2 items-center text-sm text-gray-600">
                                        <Split className="w-4 h-4" />
                                        <span>Available across all regions and cities</span>
                                      </div>
                                    </div>
                                  </div>
                                </motion.div>
                              );
                            } else {
                              return (
                                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                  {locations.map((locale, index) => {
                                    const { location, region } = locale;
                                    return (
                                      <motion.div
                                        key={index}
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.1 }}
                                        className="overflow-hidden relative bg-white rounded-xl border border-gray-100 group hover:border-pink-200"
                                      >
                                        <div className="p-4">
                                          <div className="flex justify-between items-center mb-3">
                                            <div className="flex gap-2 items-center">
                                              <div className="w-2 h-2 bg-green-500 rounded-full" />
                                              <h5 className="font-medium text-gray-900">{location}</h5>
                                            </div>
                                            <span className="text-xs font-medium text-gray-500">Branch</span>
                                          </div>
                                          <div className="space-y-2">
                                            <div className="flex gap-2 items-center text-sm text-gray-600">
                                              <Split className="w-4 h-4" />
                                              <span>{region}</span>
                                            </div>
                                          </div>
                                        </div>
                                      </motion.div>
                                    );
                                  })}
                                </div>
                              );
                            }
                          }
                        })()}
                      </motion.div>
                    )}

                    {activeTab === "terms" && (
                      <motion.div
                        key="terms"
                        className="space-y-4 sm:space-y-6"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ duration: 0.3 }}
                      >
                        {/* Redemption Instructions */}
                        {deal.redemption_instructions && (
                          <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.1 }}
                            className="overflow-hidden relative p-6 bg-gradient-to-br from-purple-50 to-white rounded-2xl border border-purple-100 group"
                          >
                            <div className="flex gap-3 items-center mb-4">
                              <div className="p-2 bg-purple-100 rounded-lg">
                                <Ticket className="w-5 h-5 text-purple-600" />
                              </div>
                              <h3 className="text-lg font-semibold text-gray-900">Redemption Instructions</h3>
                            </div>
                            <p className="leading-relaxed text-gray-600">
                              {deal.redemption_instructions}
                            </p>
                          </motion.div>
                        )}

                        {/* Refund Policy */}
                        {deal.refund_policy && (
                          <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.2 }}
                            className="overflow-hidden relative p-6 bg-gradient-to-br from-pink-50 to-white rounded-2xl border border-pink-100 group"
                          >
                            <div className="flex gap-3 items-center mb-4">
                              <div className="p-2 bg-pink-100 rounded-lg">
                                <RefreshCw className="w-5 h-5 text-pink-600" />
                              </div>
                              <h3 className="text-lg font-semibold text-gray-900">Refund Policy</h3>
                            </div>
                            <p className="leading-relaxed text-gray-600">
                              {deal.refund_policy}
                            </p>
                          </motion.div>
                        )}

                        {/* Terms & Conditions */}
                        {deal.terms_and_conditions && (
                          <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.3 }}
                            className="overflow-hidden relative p-6 bg-gradient-to-br from-blue-50 to-white rounded-2xl border border-blue-100 group"
                          >
                            <div className="flex gap-3 items-center mb-4">
                              <div className="p-2 bg-blue-100 rounded-lg">
                                <ScrollText className="w-5 h-5 text-blue-600" />
                              </div>
                              <h3 className="text-lg font-semibold text-gray-900">Terms & Conditions</h3>
                            </div>
                            <div className="space-y-3">
                              <p className="leading-relaxed text-gray-600">
                                {deal.terms_and_conditions}
                              </p>
                            </div>
                          </motion.div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Footer Actions */}
                <div className="flex-shrink-0 p-4 bg-gray-50 border-t border-gray-200 sm:p-6">
                  <div className="flex gap-3">
                    <motion.button
                      onClick={handleCopyLink}
                      className="flex flex-1 justify-center items-center px-4 py-2 text-gray-600 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {copied ? (
                        <>
                          <CheckCheck className="mr-2 w-4 h-4 text-green-600" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="mr-2 w-4 h-4" />
                          Copy Link
                        </>
                      )}
                    </motion.button>
                    <motion.button
                      className="flex justify-center items-center px-4 py-2 text-white bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg transition-all hover:from-blue-700 hover:to-purple-700"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Share2 className="mr-2 w-4 h-4" />
                      Share
                    </motion.button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </PageTransition>
      <PropertiesModal
        open={isPropertiesModalOpen}
        onClose={() => setIsPropertiesModalOpen(false)}
        properties={deal?.properties || []}
        dealTitle={deal?.title}
      />
    </>
  );
};

export default DealDetails;
